import { photoFigure, type PhotoSpec } from './primitives';

export interface NarrativePhotoPlacement {
  photo: PhotoSpec;
  paragraphIndex: number;
}

export interface NarrativePhotoFlowSpec {
  eyebrow: string;
  title: string;
  lead: string;
  paragraphs: string[];
  photos: readonly NarrativePhotoPlacement[];
  credit?: string;
}

function node<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className = '',
  text = '',
): HTMLElementTagNameMap[K] {
  const element = document.createElement(tag);
  if (className) element.className = className;
  if (text) element.textContent = text;
  return element;
}

const desktopRailMediaQuery = '(min-width: 1280px)';

interface PhotoPanel {
  element: HTMLElement;
  caption: string;
}

function photoSlot(placement: NarrativePhotoPlacement, paragraphCount: number): number {
  const { paragraphIndex } = placement;
  if (!Number.isInteger(paragraphIndex) || paragraphIndex < 0 || paragraphIndex >= paragraphCount) {
    throw new Error(
      `Invalid paragraph anchor ${paragraphIndex} for narrative photograph ${placement.photo.key}`,
    );
  }
  return paragraphIndex;
}

function createPhotoPanel(placement: NarrativePhotoPlacement, groupSize: number): PhotoPanel {
  const sourceFigure = photoFigure(placement.photo);
  const captionElement = sourceFigure.querySelector('figcaption');
  const caption = captionElement?.textContent?.trim() ?? placement.photo.caption;
  captionElement?.remove();

  const panel = node('div', 'ep-narrative-flow__media-panel');
  panel.dataset.photoKey = placement.photo.key;
  while (sourceFigure.firstChild) panel.append(sourceFigure.firstChild);

  const image = panel.querySelector<HTMLImageElement>('img');
  if (image) {
    image.sizes = groupSize > 1
      ? '(max-width: 1279px) 50vw, 180px'
      : '(max-width: 1279px) 100vw, 360px';
  }

  return { element: panel, caption };
}

function createMediaGroup(
  placements: readonly NarrativePhotoPlacement[],
  slot: number,
  sequence: number,
): HTMLElement {
  const side = sequence % 2 === 0 ? 'right' : 'left';
  const count = placements.length;
  const media = node(
    'figure',
    `ep-narrative-flow__media is-media-${side} is-media-count-${Math.min(count, 3)}`,
  );
  media.dataset.photoSide = side;
  media.dataset.flowSlot = String(slot);

  const grid = node('div', 'ep-narrative-flow__media-grid');
  const captions: string[] = [];
  for (const placement of placements) {
    const panel = createPhotoPanel(placement, count);
    grid.append(panel.element);
    if (!captions.includes(panel.caption)) captions.push(panel.caption);
  }

  const caption = node('figcaption', 'ep-narrative-flow__media-caption');
  if (captions.length > 1) caption.classList.add('is-multiple');
  for (const text of captions) {
    caption.append(node('span', 'ep-narrative-flow__caption-item', text));
  }

  media.append(grid, caption);
  return media;
}

function layoutMediaRails(stream: HTMLElement, mediaGroups: readonly HTMLElement[]): void {
  stream.style.removeProperty('min-height');
  for (const media of mediaGroups) media.style.removeProperty('top');
  if (!window.matchMedia(desktopRailMediaQuery).matches) return;

  const styles = window.getComputedStyle(stream);
  const parsedGap = Number.parseFloat(styles.getPropertyValue('--ep-media-rail-stack-gap'));
  const stackGap = Number.isFinite(parsedGap) ? parsedGap : 28;
  const nextTop: Record<'left' | 'right', number> = { left: 0, right: 0 };
  let requiredHeight = stream.getBoundingClientRect().height;

  for (const media of mediaGroups) {
    const beat = media.closest<HTMLElement>('.ep-narrative-flow__beat');
    const paragraph = beat?.querySelector<HTMLParagraphElement>(':scope > p');
    const anchorTop = paragraph?.offsetTop ?? 0;
    const side = media.dataset.photoSide === 'left' ? 'left' : 'right';
    const top = Math.max(anchorTop, nextTop[side]);
    media.style.top = `${Math.round(top)}px`;
    const bottom = top + media.offsetHeight;
    nextTop[side] = bottom + stackGap;
    requiredHeight = Math.max(requiredHeight, bottom);
  }

  stream.style.minHeight = `${Math.ceil(requiredHeight)}px`;
}

function mountMediaRailLayout(stream: HTMLElement, mediaGroups: readonly HTMLElement[]): void {
  let timeout: number | null = null;
  const schedule = (): void => {
    if (timeout !== null) window.clearTimeout(timeout);
    timeout = window.setTimeout(() => {
      timeout = null;
      layoutMediaRails(stream, mediaGroups);
    }, 0);
  };

  window.addEventListener('resize', schedule, { passive: true });
  for (const image of stream.querySelectorAll<HTMLImageElement>('img')) {
    if (!image.complete) image.addEventListener('load', schedule, { once: true });
  }
  void document.fonts.ready.then(schedule);
  schedule();
}

export function createNarrativePhotoFlow(spec: NarrativePhotoFlowSpec): HTMLElement {
  const section = node('section', 'ep-narrative-flow');

  const header = node('header', 'ep-narrative-flow__header');
  header.append(
    node('p', 'ep-eyebrow', spec.eyebrow),
    node('h3', '', spec.title),
    node('p', 'ep-narrative-flow__lead', spec.lead),
  );

  const body = node('div', 'ep-narrative-flow__body');
  const stream = node('article', 'ep-narrative-flow__stream');
  const paragraphCount = Math.max(spec.paragraphs.length, 1);
  const placementsBySlot: NarrativePhotoPlacement[][] = Array.from(
    { length: paragraphCount },
    () => [],
  );

  for (const placement of spec.photos) {
    const slot = photoSlot(placement, paragraphCount);
    placementsBySlot[slot]?.push(placement);
  }

  const mediaGroups: HTMLElement[] = [];
  for (const [index, paragraph] of spec.paragraphs.entries()) {
    const beat = node('div', 'ep-narrative-flow__beat');
    beat.append(node('p', '', paragraph));
    const placements = placementsBySlot[index] ?? [];
    if (placements.length > 0) {
      const media = createMediaGroup(placements, index, mediaGroups.length);
      mediaGroups.push(media);
      beat.append(media);
    }
    stream.append(beat);
  }

  if (spec.paragraphs.length === 0) {
    const placements = placementsBySlot[0] ?? [];
    if (placements.length > 0) {
      const beat = node('div', 'ep-narrative-flow__beat');
      const media = createMediaGroup(placements, 0, mediaGroups.length);
      mediaGroups.push(media);
      beat.append(media);
      stream.append(beat);
    }
  }

  body.append(stream);
  section.append(header, body);
  if (spec.credit) section.append(node('p', 'ep-narrative-flow__credit', spec.credit));
  mountMediaRailLayout(stream, mediaGroups);
  return section;
}
